import React from 'react'

const S  = 'rgba(43,43,43,0.10)'      // subtle neutral lines
const A  = 'rgba(27,123,63,0.30)'     // accent structural lines
const A2 = 'rgba(27,123,63,0.14)'     // faint accent fill

/* tiny helper — dimension arrow */
function DimArrow({ x1, y1, x2, y2, label, stroke = S }) {
  const dx = x2 - x1, dy = y2 - y1
  const len = Math.sqrt(dx*dx + dy*dy)
  const ux = dx/len, uy = dy/len
  const mx = (x1+x2)/2, my = (y1+y2)/2
  const aw = 5
  return (
    <g stroke={stroke} strokeWidth="0.6" fill="none">
      <line x1={x1} y1={y1} x2={x2} y2={y2} />
      <polygon points={`${x1},${y1} ${x1+ux*aw+uy*aw},${y1+uy*aw-ux*aw} ${x1+ux*aw-uy*aw},${y1+uy*aw+ux*aw}`} fill={stroke} stroke="none" />
      <polygon points={`${x2},${y2} ${x2-ux*aw+uy*aw},${y2-uy*aw-ux*aw} ${x2-ux*aw-uy*aw},${y2-uy*aw+ux*aw}`} fill={stroke} stroke="none" />
      {label && <text x={mx+4} y={my-4} fontSize="8" fontFamily="monospace" fill={stroke} style={{opacity:0.8}}>{label}</text>}
    </g>
  )
}

/* window grid helper */
function WindowGrid({ x, y, w, h, cols, rows, fill = A2 }) {
  const cw = w / cols, rh = h / rows
  const wins = []
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const px = x + c*cw + cw*0.15
      const py = y + r*rh + rh*0.15
      const pw = cw*0.7, ph = rh*0.7
      wins.push(<rect key={`${r}-${c}`} x={px} y={py} width={pw} height={ph} fill={fill} stroke={S} strokeWidth="0.4" />)
    }
  }
  return <>{wins}</>
}

export function PageHeaderBackdrop({ variant }) {

  const variants = {

    /* ── About: building cross-section elevation ── */
    about: (
      <svg viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice" style={{ width:'100%', height:'100%' }}>
        <defs>
          <pattern id="ph-dot-about" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="10" cy="10" r="0.5" fill={S} />
          </pattern>
        </defs>
        <rect width="800" height="600" fill="url(#ph-dot-about)" />

        {/* Main building elevation — 8 floors */}
        <rect x="300" y="80" width="360" height="460" fill="none" stroke={A} strokeWidth="1.2" />
        <WindowGrid x={310} y={90} w={340} h={460} cols={5} rows={8} fill={A2} />

        {/* Floor dividers */}
        {Array.from({length:7}).map((_,i) => (
          <line key={i} x1="300" y1={80+57.5*(i+1)} x2="660" y2={80+57.5*(i+1)} stroke={S} strokeWidth="0.5" />
        ))}

        {/* Ground slab */}
        <rect x="270" y="540" width="420" height="12" fill={A2} stroke={A} strokeWidth="0.8" />
        <line x1="270" y1="552" x2="690" y2="552" stroke={S} strokeWidth="0.4" strokeDasharray="4 4" />

        {/* Column lines */}
        {[300, 390, 480, 570, 660].map(x => (
          <line key={x} x1={x} y1="80" x2={x} y2="552" stroke={S} strokeWidth="0.4" strokeDasharray="2 4" />
        ))}

        {/* Left dimension — height */}
        <DimArrow x1={280} y1={80} x2={280} y2={540} label="36M" stroke={S} />

        {/* Top label */}
        <text x="350" y="68" fontSize="9" fontFamily="monospace" fill={A} letterSpacing="2" style={{opacity:0.8}}>FRONT ELEVATION</text>
        <text x="350" y="576" fontSize="8" fontFamily="monospace" fill={S} letterSpacing="1">G.L.</text>

        {/* Structural annotation dots */}
        {[300,390,480,570,660].map(x => (
          <circle key={x} cx={x} cy="80" r="3" fill="none" stroke={A} strokeWidth="0.8" />
        ))}

        {/* Scale bar */}
        <g stroke={S} strokeWidth="0.5">
          <line x1="320" y1="590" x2="500" y2="590" />
          <line x1="320" y1="586" x2="320" y2="594" />
          <line x1="410" y1="586" x2="410" y2="594" />
          <line x1="500" y1="586" x2="500" y2="594" />
          <text x="355" y="598" fontSize="7" fontFamily="monospace" fill={S}>0    5    10 M</text>
        </g>
      </svg>
    ),

    /* ── Projects: skyline elevation ── */
    projects: (
      <svg viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice" style={{ width:'100%', height:'100%' }}>
        <defs>
          <pattern id="ph-grid-proj" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M32 0H0V32" fill="none" stroke={S} strokeWidth="0.4" />
          </pattern>
        </defs>
        <rect width="800" height="600" fill="url(#ph-grid-proj)" />

        {/* Ground line */}
        <line x1="80" y1="540" x2="760" y2="540" stroke={A} strokeWidth="1" />

        {/* Building A — narrow tower */}
        <rect x="120" y="200" width="90" height="340" fill="none" stroke={A} strokeWidth="1" />
        <WindowGrid x={127} y={210} w={76} h={320} cols={2} rows={9} />

        {/* Building B — wide mid-rise */}
        <rect x="240" y="300" width="160" height="240" fill="none" stroke={S} strokeWidth="0.8" />
        <WindowGrid x={248} y={308} w={144} h={224} cols={4} rows={6} />

        {/* Building C — tall tower (main) */}
        <rect x="430" y="100" width="130" height="440" fill="none" stroke={A} strokeWidth="1.2" />
        <WindowGrid x={438} y={108} w={114} h={422} cols={3} rows={12} fill={A2} />
        {/* Penthouse */}
        <rect x="452" y="82" width="86" height="20" fill="none" stroke={A} strokeWidth="0.8" />

        {/* Building D — medium */}
        <rect x="590" y="360" width="100" height="180" fill="none" stroke={S} strokeWidth="0.7" />
        <WindowGrid x={597} y={367} w={86} h={166} cols={2} rows={5} />

        {/* Crane on building C */}
        <g stroke={A} strokeWidth="0.8" fill="none">
          <line x1="515" y1="540" x2="515" y2="50" />
          <line x1="515" y1="50" x2="660" y2="50" />
          <line x1="515" y1="50" x2="440" y2="90" />
          <line x1="630" y1="50" x2="630" y2="120" strokeDasharray="2 2" />
          {/* counterweight */}
          <rect x="432" y="82" width="16" height="10" fill={A2} />
          {/* hook */}
          <circle cx="630" cy="124" r="3" fill="none" stroke={A} />
        </g>

        {/* Height annotations */}
        <DimArrow x1={100} y1={200} x2={100} y2={540} label="42M" stroke={S} />
        <DimArrow x1={412} y1={100} x2={412} y2={540} label="58M" stroke={A} />

        {/* Label */}
        <text x="450" y="580" fontSize="8" fontFamily="monospace" fill={S} letterSpacing="1">PROJECT ELEVATION · NOT TO SCALE</text>
      </svg>
    ),

    /* ── Services: MEP systems diagram ── */
    services: (
      <svg viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice" style={{ width:'100%', height:'100%' }}>
        <defs>
          <pattern id="ph-grid-svc" width="24" height="24" patternUnits="userSpaceOnUse">
            <path d="M24 0H0V24" fill="none" stroke={S} strokeWidth="0.35" />
          </pattern>
        </defs>
        <rect width="800" height="600" fill="url(#ph-grid-svc)" />

        {/* 3-floor building shell */}
        <rect x="150" y="150" width="480" height="360" fill="none" stroke={A} strokeWidth="1" />
        {/* floor slabs */}
        <line x1="150" y1="270" x2="630" y2="270" stroke={S} strokeWidth="0.8" />
        <line x1="150" y1="390" x2="630" y2="390" stroke={S} strokeWidth="0.8" />

        {/* Civil — columns */}
        {[150, 270, 390, 510, 630].map(x => (
          <rect key={x} x={x-4} y="150" width="8" height="360" fill={A2} stroke={A} strokeWidth="0.6" />
        ))}

        {/* Mechanical — pipes (3rd floor) */}
        <g stroke="rgba(27,123,63,0.45)" strokeWidth="1.2" fill="none">
          <path d="M180 200 H380 M380 200 V240 H580" strokeDasharray="none" />
          <circle cx="380" cy="200" r="4" fill="none" stroke={A} />
          <path d="M480 200 V230" />
          {/* valve symbol */}
          <polygon points="470,228 480,214 490,228" fill={A2} stroke={A} strokeWidth="0.8" />
        </g>

        {/* Electrical — conduit (2nd floor) */}
        <g stroke={S} strokeWidth="1" fill="none" strokeDasharray="6 3">
          <path d="M180 316 H450 M450 316 V350 H580" />
        </g>
        <circle cx="180" cy="316" r="5" fill="none" stroke={S} strokeWidth="0.8" />
        <circle cx="580" cy="350" r="5" fill="none" stroke={S} strokeWidth="0.8" />

        {/* Sanitary — slope pipe (1st floor) */}
        <g stroke={S} strokeWidth="1" fill="none">
          <path d="M200 450 L480 470 L540 510" />
          <rect x="492" y="452" width="14" height="14" fill="none" stroke={S} strokeWidth="0.7" transform="rotate(-5 499 459)" />
        </g>

        {/* Legend */}
        <g fontFamily="monospace" fontSize="7" fill={S}>
          <line x1="640" y1="200" x2="660" y2="200" stroke="rgba(27,123,63,0.45)" strokeWidth="1.2" />
          <text x="665" y="203">MECHANICAL</text>
          <line x1="640" y1="220" x2="660" y2="220" stroke={S} strokeWidth="1" strokeDasharray="4 2" />
          <text x="665" y="223">ELECTRICAL</text>
          <line x1="640" y1="240" x2="660" y2="240" stroke={S} strokeWidth="1" />
          <text x="665" y="243">SANITARY</text>
        </g>
        <text x="180" y="140" fontSize="9" fontFamily="monospace" fill={A} letterSpacing="2">BUILDING SERVICES SECTION</text>
      </svg>
    ),

    /* ── Clients: Nepal map nodes ── */
    clients: (
      <svg viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice" style={{ width:'100%', height:'100%' }}>
        <defs>
          <pattern id="ph-grid-cli" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0H0V40" fill="none" stroke={S} strokeWidth="0.4" />
          </pattern>
        </defs>
        <rect width="800" height="600" fill="url(#ph-grid-cli)" />

        {/* Coordinate grid labels */}
        {[200,300,400,500,600,700].map(x => (
          <text key={x} x={x} y="30" fontSize="7" fontFamily="monospace" fill={S} textAnchor="middle">{(80 + (x-200)*0.05).toFixed(1)}°E</text>
        ))}
        {[100,200,300,400,500].map(y => (
          <text key={y} x="180" y={y} fontSize="7" fontFamily="monospace" fill={S}>{(29 - (y-100)*0.015).toFixed(1)}°N</text>
        ))}

        {/* Tick marks */}
        {[200,300,400,500,600,700].map(x => (
          <line key={x} x1={x} y1="35" x2={x} y2="550" stroke={S} strokeWidth="0.3" strokeDasharray="1 6" />
        ))}
        {[100,200,300,400,500].map(y => (
          <line key={y} x1="190" y1={y} x2="760" y2={y} stroke={S} strokeWidth="0.3" strokeDasharray="1 6" />
        ))}

        {/* Client location nodes */}
        {[
          {x:420, y:280, label:'KTM', main:true},
          {x:480, y:260, label:'BKT', main:false},
          {x:350, y:240, label:'PKR', main:false},
          {x:560, y:310, label:'BRT', main:false},
          {x:300, y:300, label:'PWT', main:false},
          {x:520, y:350, label:'JNK', main:false},
          {x:380, y:350, label:'HTP', main:false},
          {x:620, y:260, label:'ITC', main:false},
        ].map(({x,y,label,main}) => (
          <g key={label}>
            {/* Connection lines from KTM */}
            {!main && <line x1="420" y1="280" x2={x} y2={y} stroke={S} strokeWidth="0.5" strokeDasharray="3 4" />}
            <circle cx={x} cy={y} r={main?10:6} fill="none" stroke={main?A:S} strokeWidth={main?1.2:0.8} />
            {main && <circle cx={x} cy={y} r={4} fill={A2} stroke={A} strokeWidth="0.8" />}
            {!main && <circle cx={x} cy={y} r={2} fill={S} />}
            <text x={x+8} y={y-6} fontSize="7" fontFamily="monospace" fill={main?A:S}>{label}</text>
          </g>
        ))}

        {/* Compass */}
        <g transform="translate(700,480)" stroke={S} strokeWidth="0.7" fill="none">
          <circle cx="0" cy="0" r="20" />
          <line x1="0" y1="-20" x2="0" y2="20" />
          <line x1="-20" y1="0" x2="20" y2="0" />
          <polygon points="0,-20 -3,-8 3,-8" fill={A} stroke={A} strokeWidth="0.5" />
          <text x="-3" y="-23" fontSize="7" fontFamily="monospace" fill={A}>N</text>
        </g>

        <text x="220" y="570" fontSize="8" fontFamily="monospace" fill={S} letterSpacing="1">SITE COVERAGE · NEPAL</text>
      </svg>
    ),

    /* ── Highlights: camera aperture + grid ── */
    highlights: (
      <svg viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice" style={{ width:'100%', height:'100%' }}>
        <defs>
          <pattern id="ph-grid-hl" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M32 0H0V32" fill="none" stroke={S} strokeWidth="0.35" />
          </pattern>
        </defs>
        <rect width="800" height="600" fill="url(#ph-grid-hl)" />

        {/* Photo grid frames */}
        {[
          {x:120,y:100,w:180,h:140,accent:true},
          {x:316,y:100,w:120,h:140,accent:false},
          {x:452,y:100,w:180,h:140,accent:false},
          {x:120,y:256,w:120,h:180,accent:false},
          {x:256,y:256,w:180,h:180,accent:true},
          {x:452,y:256,w:180,h:80,accent:false},
          {x:452,y:352,w:180,h:84,accent:false},
          {x:120,y:452,w:340,h:100,accent:false},
          {x:476,y:452,w:156,h:100,accent:true},
        ].map(({x,y,w,h,accent},i) => (
          <g key={i}>
            <rect x={x} y={y} width={w} height={h} fill="none" stroke={accent?A:S} strokeWidth={accent?1.2:0.6} />
            {/* Corner marks */}
            {[[x,y],[x+w,y],[x,y+h],[x+w,y+h]].map(([cx,cy],j) => (
              <g key={j}>
                <line x1={cx+(cx>x+w/2?-8:0)} y1={cy} x2={cx+(cx>x+w/2?0:8)} y2={cy} stroke={accent?A:S} strokeWidth="0.6" />
                <line x1={cx} y1={cy+(cy>y+h/2?-8:0)} x2={cx} y2={cy+(cy>y+h/2?0:8)} stroke={accent?A:S} strokeWidth="0.6" />
              </g>
            ))}
          </g>
        ))}

        {/* Aperture circles */}
        <g stroke={A} strokeWidth="0.8" fill="none" opacity="0.5">
          <circle cx="650" cy="180" r="60" />
          <circle cx="650" cy="180" r="40" />
          <circle cx="650" cy="180" r="20" />
          {Array.from({length:6}).map((_,i) => {
            const a = i * Math.PI/3
            return <line key={i} x1={650+20*Math.cos(a)} y1={180+20*Math.sin(a)} x2={650+60*Math.cos(a)} y2={180+60*Math.sin(a)} />
          })}
        </g>
        <text x="150" y="574" fontSize="8" fontFamily="monospace" fill={S} letterSpacing="1">GALLERY LAYOUT · MASONRY GRID</text>
      </svg>
    ),

    /* ── Contact: building + location pin ── */
    contact: (
      <svg viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice" style={{ width:'100%', height:'100%' }}>
        <defs>
          <pattern id="ph-grid-con" width="30" height="30" patternUnits="userSpaceOnUse">
            <path d="M30 0H0V30" fill="none" stroke={S} strokeWidth="0.4" />
          </pattern>
        </defs>
        <rect width="800" height="600" fill="url(#ph-grid-con)" />

        {/* Building facade */}
        <rect x="260" y="120" width="260" height="400" fill="none" stroke={A} strokeWidth="1.2" />
        <WindowGrid x={272} y={132} w={236} h={378} cols={4} rows={9} fill={A2} />
        {/* Floor slabs */}
        {Array.from({length:8}).map((_,i) => (
          <line key={i} x1="260" y1={162+i*42} x2="520" y2={162+i*42} stroke={S} strokeWidth="0.4" />
        ))}
        {/* Ground */}
        <rect x="230" y="520" width="320" height="14" fill={A2} stroke={A} strokeWidth="0.8" />

        {/* Entrance door */}
        <rect x="356" y="468" width="68" height="52" fill="none" stroke={A} strokeWidth="0.8" />
        <line x1="390" y1="468" x2="390" y2="520" stroke={S} strokeWidth="0.4" />
        <circle cx="378" cy="494" r="2.5" fill={A2} stroke={A} strokeWidth="0.6" />

        {/* Location pin */}
        <g transform="translate(620,200)">
          <path d="M0,-40 C-22,-40 -40,-22 -40,0 C-40,22 0,60 0,60 C0,60 40,22 40,0 C40,-22 22,-40 0,-40 Z"
            fill={A2} stroke={A} strokeWidth="1.2" />
          <circle cx="0" cy="0" r="14" fill="none" stroke={A} strokeWidth="1" />
          <circle cx="0" cy="0" r="5" fill={A} />
        </g>

        {/* Dashed line from building to pin */}
        <line x1="520" y1="300" x2="580" y2="220" stroke={A} strokeWidth="0.6" strokeDasharray="4 4" />

        {/* Coordinate labels */}
        <text x="540" y="290" fontSize="8" fontFamily="monospace" fill={A}>27.7004°N</text>
        <text x="540" y="302" fontSize="8" fontFamily="monospace" fill={A}>85.3350°E</text>

        {/* Dimension */}
        <DimArrow x1={240} y1={120} x2={240} y2={520} label="HQ" stroke={S} />
        <text x="290" y="108" fontSize="9" fontFamily="monospace" fill={A} letterSpacing="2">BANESHWOR · KATHMANDU</text>
      </svg>
    ),

    /* ── Equipment: crane elevation + machinery ── */
    equipment: (
      <svg viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice" style={{ width:'100%', height:'100%' }}>
        <defs>
          <pattern id="ph-grid-eq" width="28" height="28" patternUnits="userSpaceOnUse">
            <path d="M28 0H0V28" fill="none" stroke={S} strokeWidth="0.35" />
          </pattern>
        </defs>
        <rect width="800" height="600" fill="url(#ph-grid-eq)" />

        {/* Tower crane */}
        <g stroke={A} strokeWidth="1.2" fill="none">
          {/* mast */}
          <rect x="335" y="160" width="20" height="380" fill={A2} stroke={A} strokeWidth="0.8" />
          {/* mast lattice */}
          {Array.from({length:10}).map((_,i) => (
            <line key={i} x1="335" y1={160+i*38} x2="355" y2={160+(i+1)*38} stroke={S} strokeWidth="0.5" />
          ))}
          {Array.from({length:10}).map((_,i) => (
            <line key={i} x1="355" y1={160+i*38} x2="335" y2={160+(i+1)*38} stroke={S} strokeWidth="0.5" />
          ))}
          {/* jib (boom) */}
          <line x1="345" y1="160" x2="620" y2="140" strokeWidth="1.5" />
          {/* counter-jib */}
          <line x1="345" y1="160" x2="200" y2="175" />
          {/* counter weight */}
          <rect x="180" y="172" width="22" height="14" fill={A2} />
          {/* pendant lines */}
          <line x1="620" y1="140" x2="580" y2="160" strokeWidth="0.6" />
          <line x1="520" y1="148" x2="480" y2="200" strokeWidth="0.6" strokeDasharray="3 2" />
          {/* hook block */}
          <rect x="474" y="198" width="14" height="12" fill={A2} stroke={A} strokeWidth="0.7" />
          <path d="M481 210 Q481 222 475 228 Q469 234 475 240" stroke={A} strokeWidth="0.7" fill="none" />
          {/* cable */}
          <line x1="481" y1="240" x2="481" y2="380" stroke={S} strokeWidth="0.5" strokeDasharray="2 3" />
        </g>

        {/* Base / foundation */}
        <rect x="310" y="538" width="70" height="18" fill={A2} stroke={A} strokeWidth="0.8" />
        <line x1="230" y1="556" x2="620" y2="556" stroke={S} strokeWidth="0.6" />

        {/* Concrete mixer (secondary) */}
        <g stroke={S} strokeWidth="0.8" fill="none" transform="translate(580,430)">
          <rect x="0" y="0" width="80" height="50" rx="4" />
          <ellipse cx="30" cy="25" rx="22" ry="18" stroke={S} strokeWidth="0.6" />
          <line x1="52" y1="25" x2="80" y2="25" />
          <circle cx="65" cy="55" r="8" />
          <circle cx="15" cy="55" r="8" />
        </g>

        {/* Dimension arrows */}
        <DimArrow x1={640} y1={140} x2={640} y2={556} label="62M" stroke={S} />
        <DimArrow x1={200} y1={130} x2={620} y2={130} label="JIBE 42M" stroke={S} />

        <text x="200" y="580" fontSize="8" fontFamily="monospace" fill={S} letterSpacing="1">TOWER CRANE TC-6242 · EQUIPMENT ELEVATION</text>
      </svg>
    ),

    /* ── Home hero (fallback) — kept but not usually rendered via PageHeader ── */
    home: (
      <svg viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice" style={{ width:'100%', height:'100%' }}>
        <defs>
          <pattern id="ph-dot-home" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="10" cy="10" r="0.5" fill={S} />
          </pattern>
        </defs>
        <rect width="800" height="600" fill="url(#ph-dot-home)" />
        <rect x="200" y="100" width="400" height="440" fill="none" stroke={A} strokeWidth="1.2" />
        <WindowGrid x={210} y={110} w={380} h={420} cols={6} rows={10} fill={A2} />
      </svg>
    ),
  }

  return (
    <div style={{
      position: 'absolute', right: 0, top: 0, bottom: 0,
      width: '60%', maxWidth: 780, opacity: 0.65,
      pointerEvents: 'none',
      maskImage: 'linear-gradient(to left, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)',
      WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)',
    }} className="ph-watermark">
      {variants[variant] || variants.about}
    </div>
  )
}

export default function PageHeader({ code, eyebrow, title, sub, children, badge, variant = 'about' }) {
  return (
    <header className="page-header" style={{ paddingTop: 'var(--hdr-top)', paddingBottom: 'var(--hdr-bot)', position: 'relative', borderBottom: '1px solid var(--line-soft)', overflow: 'hidden' }}>
      <PageHeaderBackdrop variant={variant} />
      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div className="mono" style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--fg-3)' }}>
          <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 3, flexShrink: 0 }}>
            <span style={{ width: 22, height: 2, background: 'var(--accent)', borderRadius: 1 }} />
            <span style={{ width: 14, height: 2, background: 'var(--accent)', opacity: 0.5, borderRadius: 1 }} />
          </span>
          <span style={{ fontSize: 13, fontWeight: 500, letterSpacing: '0.04em' }}>{eyebrow}</span>
        </div>
        <h1 className="display" style={{ fontSize: 'clamp(44px, 6.5vw, 88px)', marginTop: 28, color: 'var(--fg-0)', fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.02, maxWidth: 1050 }}>
          {title}
        </h1>
        {sub && <p style={{ marginTop: 28, maxWidth: 720, fontSize: 18, color: 'var(--fg-1)', lineHeight: 1.6 }}>{sub}</p>}
        {badge && (
          <div style={{ marginTop: 32, display: 'inline-flex', alignItems: 'center', gap: 10, padding: '8px 16px', background: '#fff', border: '1px solid var(--line-soft)', borderRadius: 999 }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--accent)' }} />
            <span style={{ fontFamily: 'var(--text)', fontSize: 13, fontWeight: 500, color: 'var(--fg-1)' }}>{badge}</span>
          </div>
        )}
        {children}
      </div>
      <style>{`@media (max-width: 720px) { .ph-watermark { opacity: 0.3 !important; } }`}</style>
    </header>
  )
}
